import type { PixelClient } from "../client.js";
import { pageContext } from "./page-context.js";

/** How the shopper reached a gallery image — shown in Live Console via `label` / `gallery_action`. */
export type GalleryAction =
  | "initial_view"
  | "thumbnail_click"
  | "main_image_click"
  | "next"
  | "prev"
  | "zoom"
  | "slide_change";

export interface GallerySlideState {
  index: number;
  total: number;
  url?: string;
}

const GALLERY_ROOT =
  ".woocommerce-product-gallery, [data-product-media], .product__media, .product-media";

/** Read the active main slide (not thumbnails) from a WooCommerce flexslider or block gallery. */
export function readGallerySlide(gallery: Element): GallerySlideState | null {
  const slides = mainSlides(gallery);
  if (slides.length === 0) return null;

  let index = slides.findIndex((s) => s.classList.contains("flex-active-slide"));
  if (index < 0) {
    const activeThumb = gallery.querySelector(".flex-control-thumbs li.flex-active");
    if (activeThumb) {
      const thumbs = [...gallery.querySelectorAll(".flex-control-thumbs li")];
      index = thumbs.indexOf(activeThumb);
    }
  }
  if (index < 0) index = 0;

  const slide = slides[index] ?? slides[0];
  const img = slide?.querySelector("img");
  const url =
    img instanceof HTMLImageElement ? img.currentSrc || img.src || undefined : undefined;

  return {
    index: Math.max(0, index),
    total: slides.length,
    ...(url ? { url } : {}),
  };
}

function mainSlides(gallery: Element): Element[] {
  const fromWrapper = [
    ...gallery.querySelectorAll(
      ".woocommerce-product-gallery__wrapper > .woocommerce-product-gallery__image",
    ),
  ];
  if (fromWrapper.length > 0) return fromWrapper;

  const fromViewport = [
    ...gallery.querySelectorAll(".flex-viewport .woocommerce-product-gallery__image"),
  ];
  if (fromViewport.length > 0) return fromViewport;

  return [...gallery.querySelectorAll(".woocommerce-product-gallery__image")].filter(
    (el) => !el.closest(".flex-control-thumbs"),
  );
}

/** Map a gallery click target to a gallery interaction type. */
export function classifyGalleryClick(target: HTMLElement, gallery: Element): GalleryAction | null {
  if (!gallery.contains(target)) return null;
  if (target.closest(".woocommerce-product-gallery__trigger")) return "zoom";
  if (target.closest(".flex-direction-nav .flex-prev, a.flex-prev")) return "prev";
  if (target.closest(".flex-direction-nav .flex-next, a.flex-next")) return "next";
  if (target.closest(".flex-control-thumbs li")) return "thumbnail_click";
  if (target.closest(".flex-viewport, .woocommerce-product-gallery__image")) {
    if (target.closest("img")) return "main_image_click";
  }
  return null;
}

function thumbIndex(target: HTMLElement, gallery: Element): number | null {
  const li = target.closest(".flex-control-thumbs li");
  if (!li || !gallery.contains(li)) return null;
  const thumbs = [...gallery.querySelectorAll(".flex-control-thumbs li")];
  const idx = thumbs.indexOf(li);
  return idx >= 0 ? idx : null;
}

function galleryLabel(action: GalleryAction, index: number, total: number): string {
  const human: Record<GalleryAction, string> = {
    initial_view: "Main image loaded",
    thumbnail_click: "Thumbnail clicked",
    main_image_click: "Main image clicked",
    next: "Next image",
    prev: "Previous image",
    zoom: "Zoom opened",
    slide_change: "Gallery slide changed",
  };
  return `Gallery ${index + 1}/${total} · ${human[action]}`;
}

/**
 * Track product gallery views with explicit interaction types (thumbnail, main, next/prev, zoom).
 * Emits `product_image_viewed` — not generic `click` — so Live Console shows gallery context.
 */
export function attachProductGallery(
  client: PixelClient,
  productId: string,
  productTitle: string | undefined,
  gallery: Element,
  imagesViewed: Set<number>,
  onView: () => void,
): () => void {
  let lastIndex = -1;
  let pendingAction: GalleryAction | null = null;
  let pendingTimer: ReturnType<typeof setTimeout> | null = null;

  const emit = (state: GallerySlideState, action: GalleryAction, nowMs: number) => {
    const repeatable: GalleryAction[] = ["thumbnail_click", "next", "prev", "main_image_click", "zoom"];
    if (state.index === lastIndex && action !== "initial_view" && !repeatable.includes(action)) return;
    lastIndex = state.index;

    if (!imagesViewed.has(state.index)) {
      imagesViewed.add(state.index);
      onView();
    }

    void client.track(
      "product_image_viewed",
      {
        product_id: productId,
        ...(productTitle ? { product_title: productTitle } : {}),
        image_index: state.index,
        image_total: state.total,
        gallery_action: action,
        label: galleryLabel(action, state.index, state.total),
        ...(state.url ? { image_url: state.url } : {}),
      },
      pageContext(nowMs),
    );
  };

  const flushPending = () => {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = null;
    const state = readGallerySlide(gallery);
    if (!state) return;
    const action = pendingAction ?? "slide_change";
    pendingAction = null;
    emit(state, action, Date.now());
  };

  const scheduleFlush = (delayMs = 400) => {
    if (pendingTimer) clearTimeout(pendingTimer);
    pendingTimer = setTimeout(flushPending, delayMs);
  };

  const onClick = (e: Event) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const action = classifyGalleryClick(target, gallery);
    if (!action) return;

    if (action === "thumbnail_click") {
      const idx = thumbIndex(target, gallery);
      const total = mainSlides(gallery).length || gallery.querySelectorAll(".flex-control-thumbs li").length;
      if (idx !== null) {
        const img = gallery.querySelectorAll(".flex-control-thumbs li img")[idx];
        const url = img instanceof HTMLImageElement ? img.currentSrc || img.src : undefined;
        emit({ index: idx, total: total || 1, ...(url ? { url } : {}) }, action, Date.now());
      }
      return;
    }

    pendingAction = action;
    scheduleFlush(action === "zoom" ? 100 : 450);
  };

  const observer = new MutationObserver(() => {
    const state = readGallerySlide(gallery);
    if (!state || state.index === lastIndex) return;
    if (pendingAction) {
      scheduleFlush(50);
      return;
    }
    emit(state, "slide_change", Date.now());
  });

  observer.observe(gallery, {
    subtree: true,
    attributes: true,
    attributeFilter: ["class", "src", "srcset", "style"],
    childList: true,
  });

  gallery.addEventListener("click", onClick, true);

  const initial = readGallerySlide(gallery);
  if (initial) emit(initial, "initial_view", Date.now());

  return () => {
    gallery.removeEventListener("click", onClick, true);
    observer.disconnect();
    if (pendingTimer) clearTimeout(pendingTimer);
  };
}

/** True when the click target is inside a product gallery (skip generic click tracking). */
export function isGalleryClick(target: HTMLElement): boolean {
  return Boolean(target.closest(GALLERY_ROOT));
}
